import { useCallback, useEffect, useState } from 'react';

/**
 * Thin wrapper over the browser Notification API for desktop notifications.
 * Degrades gracefully where unsupported or not granted (the in-app bell + toast
 * remain the primary channels). Permission is requested lazily on first use.
 */
export function useDesktopNotification(): {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  request: () => Promise<void>;
  notify: (title: string, options?: NotificationOptions & { onClick?: () => void }) => void;
} {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    supported ? Notification.permission : 'unsupported',
  );

  useEffect(() => { if (supported) setPermission(Notification.permission); }, [supported]);

  const request = useCallback(async () => {
    if (!supported || Notification.permission !== 'default') return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }, [supported]);

  const notify = useCallback((title: string, options?: NotificationOptions & { onClick?: () => void }) => {
    if (!supported || Notification.permission !== 'granted') return;
    try {
      const n = new Notification(title, { icon: '/favicon.svg', ...options });
      if (options?.onClick) n.onclick = () => { window.focus(); options.onClick?.(); n.close(); };
    } catch { /* some browsers throw if constructed without a user gesture */ }
  }, [supported]);

  return { supported, permission, request, notify };
}
